import { Skeleton, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert, { type AlertAction } from '/@/components/bayer/alert';
import {
    useAcceptAdvisorRvInvitation,
    useAdvisorRvInvitations,
    useAdvisorRvLinks,
    useCreateAdvisorRvInvitation,
    useDeclineAdvisorRvInvitation,
} from '/@/hooks/nodes';
import { useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY } from '.';
import RVOnboardingModalInitial from './initial';
import RVOnboardingModalNewInvitationsReceived from './new-invitations-received';
import { RVOnboardingModalRVPendingInvites } from './pending-invites';
import RVOnboardingModalSentInvites from './pending-invites/sent-invites';
import RVOnboardingModalRVSelector from './rv-selector';

const AdvisorOnboardingModalAndAlert = () => {
    const [open, setOpen] = useState(false);
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const advisorsRvInvitationsQuery = useAdvisorRvInvitations();
    const allSentInvitationsQuery = useAdvisorRvInvitations({ direction: 'sent' });
    const pendingInvitationsQuery = useAdvisorRvInvitations({ status: 'pending', direction: 'sent' });
    const acceptedInvitationsQuery = useAdvisorRvInvitations({ status: 'accepted', direction: 'sent' });
    const declinedInvitationsQuery = useAdvisorRvInvitations({ status: 'declined', direction: 'sent' });
    const pendingRecievedInvitationsQuery = useAdvisorRvInvitations({ status: 'pending', direction: 'received' });
    const hasLinkedAdvisors = (advisorRvLinksQuery.data?.results?.length ?? 0) > 0;
    const hasInvitedAdvisors = (advisorsRvInvitationsQuery.data?.results?.length ?? 0) > 0;
    const hasLinkedOrInvitedAdvisors = hasLinkedAdvisors || hasInvitedAdvisors;
    const wasOpened = localStorage.getItem(ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY) === 'true';
    useEffect(() => {
        if (advisorRvLinksQuery.isLoading || advisorsRvInvitationsQuery.isLoading) return;

        if (!hasLinkedOrInvitedAdvisors && !wasOpened) {
            setOpen(true);
            localStorage.setItem(ONBOARDING_RV_ADVISOR_LINKED_STORAGE_KEY, 'true');
        }
    }, [wasOpened, advisorRvLinksQuery.isLoading, advisorsRvInvitationsQuery.isLoading, hasLinkedOrInvitedAdvisors]);

    useEffect(() => {
        if (wasOpened && (pendingRecievedInvitationsQuery?.data?.count || 0) > 0) {
            setStep('new-invitations-received');
            setOpen(true);
        }
    }, [wasOpened, pendingRecievedInvitationsQuery?.data?.count]);

    // Steps
    const [step, setStep] = useState<
        'initial' | 'rv-selector' | 'pending-invites' | 'new-invitations-received' | 'sent-invites'
    >('initial');

    const createAdvisorRvInvitationMutation = useCreateAdvisorRvInvitation();
    const handleRvSelectorNext = async (rv: number) => {
        if (!rv) return;
        try {
            await createAdvisorRvInvitationMutation.trigger({ userNodeId: rv });
            setOpen(false);
            enqueueSnackbar(_('Enviaste la solicitud de vinculación'), { variant: 'success' });
        } catch {
            enqueueSnackbar(_('Ocurrio un error, intenta nuevamente.'), { variant: 'error' });
        }
    };

    // Alert title and buttons dynamically depending on user situation
    const acceptInvitationMutation = useAcceptAdvisorRvInvitation();
    const declineInvitationMutation = useDeclineAdvisorRvInvitation();
    const pendingInvitationsCount = pendingInvitationsQuery.data?.count ?? 0;
    const pendingRecievedInvitationsCount = pendingRecievedInvitationsQuery?.data?.count ?? 0;
    const firstRecievedInvitation = pendingRecievedInvitationsQuery?.data?.results?.[0];
    const lastSentInvitation = allSentInvitationsQuery.data?.results?.sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    )?.[0];
    const lastRecievedInvitation = pendingRecievedInvitationsQuery?.data?.results?.sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    )?.[0];
    const isRV = useIsValidatedRV();

    const alertTitle = useMemo(() => {
        if (hasLinkedAdvisors && !isRV) {
            if ((acceptedInvitationsQuery?.data?.count ?? 0) > 0) {
                return (
                    <>
                        <Typography component='span' color='primary' fontWeight='bold'>
                            {_('Tu RV aceptó la solicitud de vinculación. ')}
                        </Typography>
                        {_(
                            'Ahora ambos van a ver y trabajar con la información de los productores Premium que comparten.'
                        )}
                    </>
                );
            }
            return;
        }
        if (pendingRecievedInvitationsCount > 1) {
            return _('Tenés {{count}} solicitudes de vinculación pendientes de revisión.', {
                count: pendingRecievedInvitationsCount,
            });
        }
        if (pendingRecievedInvitationsCount === 1) {
            return (
                <>
                    <Typography component='span' color='primary' fontWeight='bold'>
                        {_('{{rvName}} te envió una solicitud de vinculación.', {
                            rvName: firstRecievedInvitation?.rv.name,
                        })}
                    </Typography>{' '}
                </>
            );
        }
        if (pendingInvitationsCount) {
            return (
                <>
                    <Typography component='span' color='primary' fontWeight='bold'>
                        {_('La vinculación a tu RV está pendiente.')}
                    </Typography>{' '}
                    {_('Si acepta tu solicitud, podrás compartir los perfiles y lotes de productores Premium.')}
                </>
            );
        }

        if (lastSentInvitation?.status === 'declined') {
            return (
                <Typography component='span' color='primary' fontWeight='bold'>
                    {_('La solicitud de vinculación que enviaste no fue aceptada.')}
                </Typography>
            );
        }
        return _('Vinculate con tu RV para compartir lotes e información de los productores Premium.');
    }, [
        hasLinkedAdvisors,
        isRV,
        pendingInvitationsCount,
        pendingRecievedInvitationsCount,
        firstRecievedInvitation,
        lastSentInvitation,
        acceptedInvitationsQuery,
    ]);

    const handleAcceptSingleInvitation = useCallback(() => {
        if (lastRecievedInvitation) {
            acceptInvitationMutation.trigger(lastRecievedInvitation.id);
        }
    }, [lastRecievedInvitation, acceptInvitationMutation]);

    const handleDeclineSingleInvitation = useCallback(() => {
        if (lastRecievedInvitation) {
            declineInvitationMutation.trigger(lastRecievedInvitation.id);
        }
    }, [lastRecievedInvitation, declineInvitationMutation]);

    const handleResendSingleInvitation = useCallback(() => {
        if (lastSentInvitation) {
            createAdvisorRvInvitationMutation.trigger({ userNodeId: lastSentInvitation.rv.id });
        }
    }, [lastSentInvitation, createAdvisorRvInvitationMutation]);

    const alertButtons = useMemo<AlertAction[]>(() => {
        if (hasLinkedAdvisors) return [];
        if (pendingInvitationsCount) {
            return [
                {
                    key: 'decline',
                    kind: 'link',
                    label: _('Ver solicitud'),
                    onClick: () => {
                        setStep('sent-invites');
                        setOpen(true);
                    },
                    loading: declineInvitationMutation.isMutating,
                },
            ];
        }
        if (pendingRecievedInvitationsCount === 1) {
            return [
                {
                    key: 'decline',
                    kind: 'secondary',
                    label: _('Rechazar'),
                    onClick: handleDeclineSingleInvitation,
                    loading: declineInvitationMutation.isMutating,
                },
                {
                    key: 'accept',
                    kind: 'primary',
                    label: _('Aceptar'),
                    onClick: handleAcceptSingleInvitation,
                    loading: acceptInvitationMutation.isMutating,
                },
            ];
        }
        if (pendingRecievedInvitationsCount > 1) {
            return [
                {
                    key: 'view',
                    kind: 'link',
                    label: _('Ver solicitudes'),
                    onClick: () => {
                        setStep('pending-invites');
                        setOpen(true);
                    },
                },
            ];
        }
        if (lastSentInvitation?.status === 'declined') {
            return [
                {
                    key: 'decline',
                    kind: 'link',
                    label: _('Ver solicitud'),
                    onClick: () => {
                        setStep('sent-invites');
                        setOpen(true);
                    },
                    loading: declineInvitationMutation.isMutating,
                },
            ];
        }

        return [
            {
                key: 'send',
                kind: 'link',
                label: _('Vincularme con un RV'),
                onClick: () => {
                    setStep('rv-selector');
                    setOpen(true);
                },
            },
        ];
    }, [
        hasLinkedAdvisors,
        pendingInvitationsCount,
        pendingRecievedInvitationsCount,
        lastSentInvitation,
        acceptInvitationMutation.isMutating,
        declineInvitationMutation.isMutating,
        handleAcceptSingleInvitation,
        handleDeclineSingleInvitation,
    ]);

    if (!alertTitle) return null;

    if (
        advisorRvLinksQuery.isLoading ||
        advisorsRvInvitationsQuery.isLoading ||
        pendingRecievedInvitationsQuery.isLoading ||
        allSentInvitationsQuery.isLoading ||
        declinedInvitationsQuery.isLoading
    )
        return <Skeleton variant='rectangular' width='100%' height={50} sx={{ my: 2 }} />;

    return (
        <>
            {/* Alert  */}
            <Alert severity='blue' sx={{ alignItems: 'center', my: 1 }} actions={alertButtons}>
                <Typography>{alertTitle}</Typography>
            </Alert>

            {/* Modal */}
            {/* Main Flow */}
            {step === 'initial' && !hasLinkedOrInvitedAdvisors && (
                <RVOnboardingModalInitial
                    isRV={false}
                    open={open}
                    onClose={() => setOpen(false)}
                    onNext={() => {
                        if ((pendingInvitationsQuery?.data?.count || 0) > 0) {
                            setStep('new-invitations-received');
                        }
                        setStep('rv-selector');
                    }}
                />
            )}
            {step === 'new-invitations-received' && (
                <RVOnboardingModalNewInvitationsReceived
                    pendingInvitationsQuery={pendingRecievedInvitationsQuery}
                    open={open}
                    onClose={() => setOpen(false)}
                    onInviteMoreAdvisors={() => setStep('rv-selector')}
                />
            )}
            {step === 'rv-selector' && (
                <RVOnboardingModalRVSelector
                    open={open}
                    onClose={() => setOpen(false)}
                    onNext={handleRvSelectorNext}
                    isSending={createAdvisorRvInvitationMutation.isMutating}
                />
            )}

            {/* Secondary Flow */}
            {step === 'pending-invites' && (
                <RVOnboardingModalRVPendingInvites
                    open={open}
                    onClose={() => setOpen(false)}
                    onInviteMoreRvs={() => setStep('rv-selector')}
                />
            )}

            {step === 'sent-invites' && (
                <RVOnboardingModalSentInvites
                    open={open}
                    onClose={() => setOpen(false)}
                    invitation={lastSentInvitation}
                    onResendInvitation={handleResendSingleInvitation}
                    onVinculateWithOtherRv={() => setStep('rv-selector')}
                />
            )}
        </>
    );
};

export default AdvisorOnboardingModalAndAlert;
